export type RoomSeat = 0 | 1;
export type RoomPhase = "waiting" | "playing" | "paused" | "result";

export const RANK_TITLES = [
  "5級",
  "4級",
  "3級",
  "2級",
  "1級",
  "初段",
  "2段",
  "3段",
  "4段",
  "5段",
  "6段",
  "7段",
  "8段",
  "9段",
  "将軍",
  "元帥",
  "西川",
  "神",
] as const;

export type RankTitle = (typeof RANK_TITLES)[number];
export type RankGroup = "E" | "D" | "C" | "B" | "A" | "S";

export type RoomQuality = {
  rtt: number;
  jitter: number;
  loss: number;
  samples: number;
};

export type RoomMember = {
  id: string;
  name: string;
  trip: string;
  rank?: RankTitle;
  rankGroup?: RankGroup;
  record?: RoomRecord;
  connected: boolean;
  seat: RoomSeat | null;
  ready: boolean;
  spectatorOnly?: boolean;
  quality: RoomQuality;
};

export type RoomPlayer = { id: string; seat: RoomSeat };

export type RoomMatch = {
  id: string;
  game: "tetris";
  phase: RoomPhase;
  seed: string;
  players: RoomPlayer[];
  matchHostId: string;
  startedAt: number;
};

export type RoomView = {
  roomCode: string;
  roomHostId: string | null;
  matchHostId: string | null;
  members: RoomMember[];
  match: RoomMatch | null;
};

export type RoomRecord = {
  games: number;
  wins: number;
  losses: number;
  draws: number;
  rank: RankTitle;
  rankGroup: RankGroup;
  rankIndex: number;
  experience: number;
  winStreak: number;
  lossStreak: number;
  room6Locked: boolean;
};

export type HeldTetrisInput = {
  left: boolean;
  right: boolean;
  softDrop: boolean;
  hardDrop: boolean;
  rotateCW: boolean;
  rotateCCW: boolean;
};

export type TetrisView = {
  playerId: string;
  tick: number;
  board: number[][];
  active: { piece: number; rotation: number; x: number; y: number };
  nextPiece: number;
  pending: number;
  received: number;
  lines: number;
  /** Added after the initial room protocol; old clients may omit it. */
  pieces?: number;
  topOut: boolean;
  /** Optional during the rolling deploy; used to trigger local battle audio. */
  lastLockSeq?: number;
  lastClearedLines?: number;
};

export type JsonValue = null | boolean | number | string | JsonValue[] | { [key: string]: JsonValue };

export type GameRoomClientMessage =
  | { type: "join"; profileId: string; name: string; trip?: string; rejoinToken?: string; queue?: "casual" }
  | { type: "leave" }
  | { type: "seat"; seat: RoomSeat | null }
  | { type: "ready"; ready: boolean }
  | { type: "chat"; text: string; color?: string }
  | { type: "record" }
  | { type: "cpu-result"; matchId: string; result: "win" | "loss" | "draw" }
  | { type: "quality"; quality: RoomQuality }
  | { type: "ping"; sentAt: number }
  | { type: "input"; matchId: string; input: HeldTetrisInput; inputSeq: number }
  | { type: "snapshot"; matchId: string; tick: number; states: TetrisView[]; checkpoints?: { [playerId: string]: JsonValue } }
  | { type: "result"; matchId: string; winners: string[]; reason: string };

export type ChatMessage = {
  id: string;
  memberId: string;
  name: string;
  trip: string;
  text: string;
  at: number;
  system?: boolean;
  systemRole?: "winner" | "loser" | "notice";
  color?: string;
  record?: boolean;
};

export type GameRoomServerMessage =
  | { type: "welcome"; memberId: string; rejoinToken: string; resumed: boolean; room: RoomView; stats: RoomRecord }
  | { type: "room"; room: RoomView }
  | { type: "stats"; stats: RoomRecord }
  | { type: "chat"; message: ChatMessage }
  | { type: "input"; senderId: string; matchId: string; input: HeldTetrisInput; inputSeq: number }
  | { type: "match-start"; match: RoomMatch }
  | { type: "match-state"; matchId: string; tick: number; states: TetrisView[] }
  | { type: "match-paused"; matchId: string; reason: string; matchHostId: string | null }
  | { type: "match-migrated"; matchId: string; matchHostId: string; tick: number; checkpoints: { [playerId: string]: JsonValue } }
  | { type: "match-result"; matchId: string; winners: string[]; reason: string }
  | { type: "pong"; sentAt: number }
  | { type: "error"; code: string; message: string; recoverable: boolean };

export function isServerMessage(value: unknown): value is GameRoomServerMessage {
  return typeof value === "object" && value !== null && typeof (value as { type?: unknown }).type === "string";
}
